// src/pages/LicencaNova.jsx
import React, { useEffect, useState, useContext } from "react";
import {
  Box, Typography, Grid, TextField, MenuItem, FormControlLabel, Checkbox,
  Button, Paper
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import { AuthContext } from "../context/AuthContext";
import BlocoKitBlindagem from '../components/BlocoKitBlindagem';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';

/* =================== Helpers comuns =================== */
function dataHojeSP() {
  const hojeSP = new Date(
    new Date().toLocaleString("en-US", { timeZone: "America/Sao_Paulo" })
  );
  const ano = hojeSP.getFullYear();
  const mes = String(hojeSP.getMonth() + 1).padStart(2, '0');
  const dia = String(hojeSP.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
}
function somarDias(data, dias) {
  const d = new Date(data);
  d.setDate(d.getDate() + dias);
  return d.toISOString().split("T")[0];
}

function formatarDataCurta(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return d.toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatarDataDia(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return d.toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
  });
}

function formatarLinhaPerm(ativo, vencimento, nomePerm) {
  if (!ativo) return `NÃO ${nomePerm}`;
  const data = vencimento ? formatarDataDia(vencimento) : null;
  return data ? `SIM até ${data} ${nomePerm}` : `SIM ${nomePerm}`;
}

/* =================== Bloco Rotinas (com atalhos +30/+365) =================== */
function BlocoRotinas({ form, setForm }) {
  const hoje = dataHojeSP();
  const [dataMassa, setDataMassa] = useState(hoje);

  const rotinas = [
    { key: "envio_contatos", label: "Envio a Contatos" },
    { key: "envio_grupos", label: "Envio a Grupos" },
    { key: "agendamento", label: "Agendamento" },
    { key: "mega_filter", label: "MegaFilter" },
    { key: "chatbot", label: "ChatBot MultiContas" },
    { key: "gmaps", label: "Gmaps" },
    { key: "suporte_tecnico", label: "Suporte Técnico" },
    { key: "grupos_internet", label: "Grupos na Internet" },
    { key: "videos", label: "Vídeos" },
  ];

  const algumMarcado = rotinas.some(({ key }) => !!form[key]);

  const handleCheckboxChange = e => {
    const { name, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: checked }));
  };
  const handleDateChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const alterarVencimentoEmMassa = dias => {
    const baseHoje = new Date(dataHojeSP());
    setForm(prev => {
      const novo = { ...prev };
      rotinas.forEach(({ key }) => {
        if (novo[key]) {
          const d = new Date(baseHoje);
          d.setDate(d.getDate() + dias);
          novo[`${key}_vencimento`] = d.toISOString().split("T")[0];
        }
      });
      return novo;
    });
  };

  const aplicarDataEmMassa = () => {
    if (!dataMassa) return;
    setForm(prev => {
      const novo = { ...prev };
      rotinas.forEach(({ key }) => {
        if (novo[key]) {
          novo[`${key}_vencimento`] = dataMassa;
        }
      });
      return novo;
    });
  };

  return (
    <Box mt={4} mb={4} p={2} border="1px solid #ccc" borderRadius={2}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Permitir acesso às Rotinas
      </Typography>
      <Grid container spacing={2}>
        {rotinas.map(({ key, label }) => (
          <React.Fragment key={key}>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!form[key]}
                    onChange={handleCheckboxChange}
                    name={key}
                  />
                }
                label={label}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Data de Vencimento"
                type="date"
                name={`${key}_vencimento`}
                value={form[`${key}_vencimento`] || hoje}
                onChange={handleDateChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={!form[key]}
              />
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
      <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
        <Grid item xs={12} sm={3}>
          <Box display="flex" gap={2}>
            <Button variant="outlined" onClick={() => alterarVencimentoEmMassa(30)}>
              +30 dias
            </Button>
            <Button variant="outlined" onClick={() => alterarVencimentoEmMassa(365)}>
              +365 dias
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            type="date"
            label="Definir data"
            value={dataMassa}
            onChange={(e) => setDataMassa(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <Button
            variant="contained"
            onClick={aplicarDataEmMassa}
            disabled={!dataMassa || !algumMarcado}
            fullWidth
            size="medium"
          >
            APLICAR EM TODOS
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

/* =================== Bloco Permissões Especiais (com botões) =================== */
function BlocoPermissoesEspeciais({ form, setForm, isAdmin, onSalvarHistorico }) {
  const hoje = dataHojeSP();

  const perms = [
    { key: "pode_usar_flow", label: "Flow Editor" },
    { key: "pode_usar_gpt", label: "GPT" },
    { key: "pode_usar_aquecimento", label: "Aquecimento Comunitário" },
    { key: "pode_usar_aquecimento_grupos", label: "Aquecimento Comunitário de Grupos" },
  ];

  const [dataPermsMassa, setDataPermsMassa] = useState(hoje);
  const algumMarcadoPerms = perms.some(({ key }) => !!form[key]);

  const onFlag = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.checked }));
  const onDate = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const alterarVencimentoPerms = (dias) => {
    const baseHoje = new Date(dataHojeSP());
    setForm(prev => {
      const novo = { ...prev };
      perms.forEach(({ key }) => {
        if (novo[key]) {
          const d = new Date(baseHoje);
          d.setDate(d.getDate() + dias);
          novo[`${key}_vencimento`] = d.toISOString().split("T")[0];
        }
      });
      return novo;
    });
  };

  const aplicarDataPerms = () => {
    if (!dataPermsMassa) return;
    setForm(prev => {
      const novo = { ...prev };
      perms.forEach(({ key }) => {
        if (novo[key]) {
          novo[`${key}_vencimento`] = dataPermsMassa;
        }
      });
      return novo;
    });
  };

  return (
    <Box mt={4} mb={4} p={2} border="1px solid #ccc" borderRadius={2}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Permissões Especiais
      </Typography>

      <Grid container spacing={2}>
        {perms.map(({ key, label }) => (
          <React.Fragment key={key}>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!form[key]}
                    onChange={onFlag}
                    name={key}
                    disabled={!isAdmin}
                  />
                }
                label={label}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Data de Vencimento"
                type="date"
                name={`${key}_vencimento`}
                value={form[`${key}_vencimento`] || hoje}
                onChange={onDate}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={!form[key] || !isAdmin}
              />
            </Grid>
          </React.Fragment>
        ))}
      </Grid>

      <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
        <Grid item xs={12} sm={3}>
          <Box display="flex" gap={2}>
            <Button variant="outlined" onClick={() => alterarVencimentoPerms(30)} disabled={!isAdmin}>
              +30 dias
            </Button>
            <Button variant="outlined" onClick={() => alterarVencimentoPerms(365)} disabled={!isAdmin}>
              +365 dias
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            type="date"
            label="Definir data"
            value={dataPermsMassa}
            onChange={(e) => setDataPermsMassa(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            disabled={!isAdmin}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <Button
            variant="contained"
            onClick={aplicarDataPerms}
            disabled={!isAdmin || !dataPermsMassa || !algumMarcadoPerms}
            fullWidth
          >
            APLICAR EM TODOS
          </Button>
        </Grid>

        <Grid item xs={12} sm={3}>
          {isAdmin && onSalvarHistorico && (
            <Button
              variant="contained"
              color="secondary"
              onClick={onSalvarHistorico}
              fullWidth
            >
              Salvar no histórico
            </Button>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

function BlocoHistoricoPermissoes({ historico, onClear }) {
  if (!historico || historico.length === 0) return null;

  return (
    <Box mt={2} mb={4} p={2} border="1px solid #ddd" borderRadius={2}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6">
          Histórico – Permissões Especiais
        </Typography>

        <Button
          size="small"
          color="error"
          variant="outlined"
          onClick={onClear}
        >
          Limpar histórico
        </Button>
      </Box>

      <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
        {historico.map((h) => {
          const linhasPerm = [
            formatarLinhaPerm(
              h.pode_usar_flow,
              h.pode_usar_flow_vencimento,
              'Flow'
            ),
            formatarLinhaPerm(
              h.pode_usar_gpt,
              h.pode_usar_gpt_vencimento,
              'GPT'
            ),
            formatarLinhaPerm(
              h.pode_usar_aquecimento,
              h.pode_usar_aquecimento_vencimento,
              'Aquecimento'
            ),
            formatarLinhaPerm(
              h.pode_usar_aquecimento_grupos,
              h.pode_usar_aquecimento_grupos_vencimento,
              'Aquecimento grupos'
            ),
          ];

          return (
            <Paper
              key={h.id}
              variant="outlined"
              sx={{ p: 1.5, mb: 1 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatarDataCurta(h.criado_em)} – {h.usuario_nome || 'Sistema'}{' '}
                {h.usuario_email ? `(${h.usuario_email})` : ''}
              </Typography>

              {linhasPerm.map((linha, idx) => (
                <Typography key={idx} variant="caption" display="block">
                  {linha}
                </Typography>
              ))}

              {h.obs && (
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ mt: 0.5, fontStyle: 'italic' }}
                >
                  {h.obs}
                </Typography>
              )}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}

/* =================== Página principal =================== */
export default function LicencaNova() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editando = Boolean(id);
  const hoje = dataHojeSP();

  // agora pegando também o usuário para usar creditos_kit_blindagem
  const { user, userTipo, podeUsarKitBlindagem } = useContext(AuthContext);
  const isAdmin = userTipo === 'admin';
  
  const [copied, setCopied] = useState(false);
  const [historicoPerms, setHistoricoPerms] = useState([]);
  
  // controle de uso de crédito de kit
  const [usandoCredito, setUsandoCredito] = useState(false);
  // 🔧 novo: gatilho para recarregar o bloco do kit
  const [kitRefresh, setKitRefresh] = useState(0);
  // 🔧 novo: saldo local para refletir na UI
  const [saldoCreditosLocal, setSaldoCreditosLocal] = useState(
    user?.creditos_kit_blindagem ?? 0
  );
    
  const podeEditarKit = isAdmin || !!podeUsarKitBlindagem || saldoCreditosLocal > 0;


  const [form, setForm] = useState(() => {
    const inicial = {
      chave: "",
      chave_usuario: localStorage.getItem("chave_usuario") || "",
      criado_em: hoje,
      data_renovacao: "",
      validade: hoje,
      qt_dias: "",
      cliente_nome: "",
      email: "",
      senha: "",
      telefone: "",
      anotacao: "",
      anotacao_admin: "",
      status: "ativo",
      produto: "Disparador de mensagens",
      valor: 497,
      tipo_licenca: "Premium",
      tipo_plano: "Anual",
      max_ativacoes: 1,
      podeVariasIntacias: true,
      qtdContasEdge: 20,
      pode_cadastrar_grupos_aquecimento: false,
      envio_contatos: true,
      envio_contatos_vencimento: somarDias(hoje, 365),
      envio_grupos: true,
      envio_grupos_vencimento: somarDias(hoje, 365),
      agendamento: true,
      agendamento_vencimento: somarDias(hoje, 365),
      mega_filter: true,
      mega_filter_vencimento: somarDias(hoje, 365),
      chatbot: true,
      chatbot_vencimento: somarDias(hoje, 365),
      gmaps: true,
      gmaps_vencimento: somarDias(hoje, 365),
      suporte_tecnico: true,
      suporte_tecnico_vencimento: somarDias(hoje, 365),
      grupos_internet: true,
      grupos_internet_vencimento: somarDias(hoje, 365),
      videos: true,
      videos_vencimento: somarDias(hoje, 365),
      kit_blindagem: true,
      kit_blindagem_vencimento: somarDias(hoje, 30),
      pode_usar_flow: true,
      pode_usar_flow_vencimento: somarDias(hoje, 30),
      pode_usar_gpt: true,
      pode_usar_gpt_vencimento: somarDias(hoje, 30),
      pode_usar_aquecimento: true,
      pode_usar_aquecimento_vencimento: somarDias(hoje, 30),
      pode_usar_aquecimento_grupos: true,
      pode_usar_aquecimento_grupos_vencimento: somarDias(hoje, 30),

    };
    return inicial;
  });

  
  const [errors, setErrors] = useState({});
  const [vencimentoOriginal, setVencimentoOriginal] = useState('');

  const handleCopyChave = () => {
    navigator.clipboard.writeText(form.chave);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  async function carregarLicenca(pk) {
    try {
      const { data } = await api.get(`/licencas/${pk}`);
      setForm(prev => ({
        ...prev,
        ...data,
        chave: data.chave,
        chave_usuario: data.chave_usuario,
        criado_em: data.criado_em ? data.criado_em.split("T")[0] : "",
        data_renovacao: data.data_renovacao?.split("T")[0] || "",
        validade: data.validade?.split("T")[0] || hoje,
        cliente_nome: data.cliente_nome,
        email: data.email || "",
        senha: data.senha,
        telefone: data.telefone || "",
        anotacao: data.anotacao || "",
        anotacao_admin: data.anotacao_admin || "",
        status: data.status,
        produto: data.produto,
        valor: Number(data.valor),
        tipo_licenca: data.tipo_licenca,
        tipo_plano: data.tipo_plano,
        max_ativacoes: Number(data.max_ativacoes),
        podeVariasIntacias: !!data.podeVariasIntacias,
        qtdContasEdge: Number(data.qtdContasEdge),
        pode_cadastrar_grupos_aquecimento: !!data.pode_cadastrar_grupos_aquecimento,
        envio_contatos: !!data.envio_contatos,
        envio_contatos_vencimento: data.envio_contatos_vencimento?.split("T")[0] || hoje,
        envio_grupos: !!data.envio_grupos,
        envio_grupos_vencimento: data.envio_grupos_vencimento?.split("T")[0] || hoje,
        agendamento: !!data.agendamento,
        agendamento_vencimento: data.agendamento_vencimento?.split("T")[0] || hoje,
        mega_filter: !!data.mega_filter,
        mega_filter_vencimento: data.mega_filter_vencimento?.split("T")[0] || hoje,
        chatbot: !!data.chatbot,
        chatbot_vencimento: data.chatbot_vencimento?.split("T")[0] || hoje,
        gmaps: !!data.gmaps,
        gmaps_vencimento: data.gmaps_vencimento?.split("T")[0] || hoje,
        suporte_tecnico: !!data.suporte_tecnico,
        suporte_tecnico_vencimento: data.suporte_tecnico_vencimento?.split("T")[0] || hoje,
        grupos_internet: !!data.grupos_internet,
        grupos_internet_vencimento: data.grupos_internet_vencimento?.split("T")[0] || hoje,
        videos: !!data.videos,
        videos_vencimento: data.videos_vencimento?.split("T")[0] || hoje,
        kit_blindagem: !!data.kit_blindagem,
        kit_blindagem_vencimento: data.kit_blindagem_vencimento?.split("T")[0] || hoje,
        pode_usar_flow: !!data.pode_usar_flow,
        pode_usar_flow_vencimento: data.pode_usar_flow_vencimento?.split("T")[0] || hoje,
        pode_usar_gpt: !!data.pode_usar_gpt,
        pode_usar_gpt_vencimento: data.pode_usar_gpt_vencimento?.split("T")[0] || hoje,
        pode_usar_aquecimento: !!data.pode_usar_aquecimento,
        pode_usar_aquecimento_vencimento: data.pode_usar_aquecimento_vencimento?.split("T")[0] || hoje,
        pode_usar_aquecimento_grupos: !!data.pode_usar_aquecimento_grupos,
        pode_usar_aquecimento_grupos_vencimento: data.pode_usar_aquecimento_grupos_vencimento?.split("T")[0] || hoje,
      }));
      setVencimentoOriginal(data.validade?.split("T")[0] || hoje);
    } catch {
      alert("Erro ao carregar licença.");
      navigate("/licencas");
    }
  }

  async function carregarHistoricoPerms(idLicenca) {
    if (!isAdmin) return;

    try {
      const { data } = await api.get(`/licencas/${idLicenca}/permissoes-historico`);
      const lista = Array.isArray(data)
        ? data
        : Array.isArray(data?.rows)
          ? data.rows
          : Array.isArray(data?.historico)
            ? data.historico
            : [];
      setHistoricoPerms(lista);
    } catch (e) {
      console.error('Erro ao carregar histórico de permissões:', e);
      setHistoricoPerms([]);
    }
  }

  useEffect(() => {
    if (editando) {
      carregarLicenca(id);
      carregarHistoricoPerms(id);
    } else {
      setForm(prev => ({ ...prev, chave: gerarChave() }));
      setVencimentoOriginal("");
      setHistoricoPerms([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setSaldoCreditosLocal(user?.creditos_kit_blindagem ?? 0);
  }, [user]);

  async function carregarSaldoCreditosKit() {
    try {
      const { data } = await api.get('/kit-blindagem/me/creditos');
      setSaldoCreditosLocal(Number(data?.creditos_kit_blindagem ?? 0));
    } catch (e) {
      console.error('Erro ao carregar saldo de créditos kit:', e);
      setSaldoCreditosLocal(0);
    }
  }
  
  useEffect(() => {
    carregarSaldoCreditosKit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setErrors(prev => ({ ...prev, [name]: "" }));

    if (name === "qt_dias") {
      if (value === "" || value === null) {
        setForm(p => ({
          ...p,
          qt_dias: "",
          validade: editando ? vencimentoOriginal : p.validade
        }));
        return;
      }
      let dias = Math.max(1, Math.min(Number(value), 1000));
      const base = form.data_renovacao ? new Date(form.data_renovacao) : new Date();
      base.setDate(base.getDate() + dias);
      setForm(p => ({
        ...p,
        qt_dias: dias,
        validade: base.toISOString().split("T")[0]
      }));
      return;
    }
    if (name === "max_ativacoes") {
      if (value === "") {
        setForm(p => ({ ...p, max_ativacoes: "" }));
      } else {
        const n = Number(value);
        const val = Math.max(1, Math.min(isNaN(n) ? 1 : n, 20));
        setForm(p => ({ ...p, max_ativacoes: val }));
      }
      return;
    }
    if (name === "qtdContasEdge") {
      let val = value === "" ? 1 : Math.max(1, Math.min(Number(value), 10000));
      setForm(p => ({ ...p, qtdContasEdge: val }));
      return;
    }
    if (name === "valor") {
      if (value === "") {
        setForm(p => ({ ...p, valor: "" }));
        return;
      }
      const n = Number(value);
      setForm(p => ({ ...p, valor: isNaN(n) ? "" : n }));
      return;
    }

    setForm(p => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const validate = () => {
    const errs = {};
    if (!form.chave) errs.chave = "Obrigatório";
    if (!form.cliente_nome) errs.cliente_nome = "Obrigatório";
    if (!form.validade) errs.validade = "Obrigatório";
    setErrors(errs);
    return !Object.keys(errs).length;
  };

  const buildPayload = () => {
    const base = {
      ...form,
      data_renovacao: form.data_renovacao || null,
      telefone: form.telefone || null,
      email: form.email || null,
      anotacao: form.anotacao || null,
      anotacao_admin: form.anotacao_admin || null,
      valor: form.valor === "" ? 0 : Number(form.valor),
      qt_dias: Number(form.qt_dias),
      podeVariasIntacias: form.podeVariasIntacias ? 1 : 0,
      qtdContasEdge: Number(form.qtdContasEdge),
      max_ativacoes:
        form.max_ativacoes === "" ? 1 :
          Math.max(1, Math.min(Number(form.max_ativacoes), 20)),
    };
  
    if (!isAdmin) delete base.anotacao_admin;
  
    return base;
  };
  
  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = buildPayload();

    try {
      if (editando) {
        await api.put(`/licencas/${id}`, payload);
        alert("Licença atualizada com sucesso!");

        if (isAdmin) {
          await carregarHistoricoPerms(id);
        }
      } else {
        await api.post("/licencas", payload);
        alert("Nova licença cadastrada com sucesso!");
        navigate(`/licencas/novo/${payload.chave}`);
      }
    } catch (err) {
      alert(err.response?.data?.error || "Erro ao salvar licença.");
    }
  };

  const handleSalvarHistoricoPerms = async () => {
    if (!editando || !isAdmin) return;

    const payload = {
      ...buildPayload(),
      salvarHistoricoPermissoes: true,
    };

    try {
      await api.put(`/licencas/${id}`, payload);
      await carregarHistoricoPerms(id);
      alert('Histórico de permissões salvo.');
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Erro ao salvar histórico de permissões.');
    }
  };

  const handleKitBlindagemSalvo = async () => {
    if (editando) {
      await carregarLicenca(id);
      // força o bloco a recarregar histórico
      setKitRefresh((c) => c + 1);
      // 🔧 novo: recarrega o saldo de créditos depois de salvar
      await carregarSaldoCreditosKit();
    }
  };

  // usar 1 crédito de kit blindagem desta licença

  const handleConfirmarKitComCredito = async (arg = false) => {
    const semConfirmacao = arg === true; // só true pula confirmação (event NÃO entra aqui)
  
    if (!editando) {
      alert('Só é possível usar crédito em licenças já salvas.');
      return;
    }
  
    try {
      if (saldoCreditosLocal <= 0) {
        alert('Você não possui créditos disponíveis.');
        return;
      }
  
      if (!semConfirmacao) {
        const ok = window.confirm(
          'Confirmar KIT BLINDAGEM desta licença usando 1 crédito?'
        );
        if (!ok) return;
      }
  
      setUsandoCredito(true);
  
      const { data } = await api.post(`/kit-blindagem/${form.chave}/usar-credito`);
  
      alert(data?.mensagem || 'Kit confirmado usando crédito.');
  
      if (typeof data?.novoSaldo === 'number') {
        setSaldoCreditosLocal(data.novoSaldo);
      } else {
        setSaldoCreditosLocal((prev) => Math.max(0, prev - 1));
      }
  
      await carregarLicenca(id);
      setKitRefresh((c) => c + 1);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Erro ao usar crédito.');
    } finally {
      setUsandoCredito(false);
    }
  };
    
  function gerarChave() {
    return "XXXX-XXXX-XXXX-XXXX".replace(/X/g, () =>
      Math.floor(Math.random() * 16).toString(16).toUpperCase()
    );
  }

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>
        {editando ? "Editar Licença" : "Nova Licença"}
      </Typography>
      <Box
        component="form"
        autoComplete="off"
        onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
      >
        <input type="text" name="fake-username" autoComplete="username email" style={{ display: 'none' }} />
        <input type="password" name="fake-password" autoComplete="new-password" style={{ display: 'none' }} />

        <Paper sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
          <Paper variant="outlined" sx={{ mt: 4, mb: 4, p: 2, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Dados Principais
            </Typography>

            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Chave"
                      name="chave"
                      value={form.chave}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copiar para área de transferência">
                              <IconButton
                                onClick={handleCopyChave}
                                edge="end"
                                size="small"
                                tabIndex={-1}
                              >
                                <ContentCopyIcon
                                  fontSize="small"
                                  color={copied ? "success" : "action"}
                                />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                        sx: { fontFamily: "monospace", letterSpacing: "0.05em" }
                      }}
                      helperText={copied ? "Copiado!" : ""}
                      fullWidth
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Produto"
                      name="produto"
                      value={form.produto}
                      onChange={handleChange}
                      fullWidth
                      autoComplete="off"
                    >
                      <MenuItem value="Disparador de mensagens">
                        Disparador de mensagens
                      </MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Tipo de Licença"
                      select
                      name="tipo_licenca"
                      value={form.tipo_licenca}
                      onChange={handleChange}
                      fullWidth
                      autoComplete="off"
                      error={!!errors.tipo_licenca}
                      helperText={errors.tipo_licenca}
                    >
                      <MenuItem value="Básico">Básico</MenuItem>
                      <MenuItem value="Premium">Premium</MenuItem>
                      <MenuItem value="Ultimate">Ultimate</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Qtde Ativações"
                      type="number"
                      name="max_ativacoes"
                      value={form.max_ativacoes}
                      onChange={handleChange}
                      fullWidth
                      autoComplete="off"
                      inputProps={{ min: 1, max: 20 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Tipo de Plano"
                      name="tipo_plano"
                      value={form.tipo_plano}
                      onChange={handleChange}
                      fullWidth
                      autoComplete="off"
                      error={!!errors.tipo_plano}
                      helperText={errors.tipo_plano}
                    >
                      <MenuItem value="Mensal">Mensal</MenuItem>
                      <MenuItem value="Semestral">Semestral</MenuItem>
                      <MenuItem value="Anual">Anual</MenuItem>
                      <MenuItem value="Teste">Teste</MenuItem>
                      <MenuItem value="Gratuita">Gratuita</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Valor"
                      type="number"
                      name="valor"
                      value={form.valor}
                      onChange={handleChange}
                      fullWidth
                      autoComplete="off"
                      inputProps={{ step: 50 }}
                      error={!!errors.valor}
                      helperText={errors.valor}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Criado em"
                      type="date"
                      name="criado_em"
                      value={form.criado_em}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Renovação"
                      type="date"
                      name="data_renovacao"
                      value={form.data_renovacao}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      autoComplete="off"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Vencimento"
                      type="date"
                      name="validade"
                      value={form.validade}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.validade}
                      helperText={errors.validade}
                      fullWidth
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Qt Dias"
                      type="number"
                      name="qt_dias"
                      value={form.qt_dias}
                      onChange={handleChange}
                      fullWidth
                      autoComplete="off"
                      inputProps={{ min: 1, max: 1000 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Nome do Cliente"
                      name="cliente_nome"
                      value={form.cliente_nome}
                      onChange={handleChange}
                      fullWidth
                      error={!!errors.cliente_nome}
                      helperText={errors.cliente_nome}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Telefone"
                      name="telefone"
                      value={form.telefone || ""}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      fullWidth
                      autoComplete="off"
                      inputProps={{ autoComplete: "off" }}
                      autoCapitalize="none"
                      spellCheck={false}
                      disabled={!isAdmin}
                      InputProps={{ readOnly: !isAdmin }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Senha"
                      type="password"
                      name="senha"
                      value={form.senha}
                      onChange={handleChange}
                      fullWidth
                      autoComplete="new-password"
                      inputProps={{ autoComplete: "new-password" }}
                      disabled={!isAdmin}
                      InputProps={{ readOnly: !isAdmin }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Qtd de Contas Edge Pode Abrir"
                      type="number"
                      name="qtdContasEdge"
                      value={form.qtdContasEdge}
                      onChange={handleChange}
                      fullWidth
                      autoComplete="off"
                      inputProps={{ min: 1, max: 10000 }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="podeVariasIntacias"
                          checked={!!form.podeVariasIntacias}
                          onChange={handleChange}
                        />
                      }
                      label="Permitir várias instâncias"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              >
                <Box sx={{ flex: 2, display: "flex" }}>
                  <TextField
                    label="Anotação (Representante)"
                    name="anotacao"
                    value={form.anotacao}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    minRows={4}
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-root": {
                        alignItems: "flex-start",
                        height: "100%",
                      },
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1, display: "flex" }}>
                  <TextField
                    label="Anotação Administrativa / Histórico"
                    name="anotacao_admin"
                    value={form.anotacao_admin}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    minRows={3}
                    disabled={!isAdmin}
                    InputProps={{ readOnly: !isAdmin }}
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-root": {
                        alignItems: "flex-start",
                        height: "100%",
                      },
                    }}
                    helperText={
                      !isAdmin
                        ? "Somente administrador pode editar. Você pode copiar o conteúdo."
                        : ""
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <BlocoRotinas
            form={form}
            setForm={setForm}
          />

          <BlocoKitBlindagem
            form={form}
            setForm={setForm}
            podeEditarKit={podeEditarKit}
            isAdmin={isAdmin}
            editando={editando}
            onSaved={handleKitBlindagemSalvo}
            saldoCreditos={saldoCreditosLocal}
            onUsarCredito={handleConfirmarKitComCredito}
            usandoCredito={usandoCredito}
            kitRefresh={kitRefresh}
            podeUsarKitBlindagem={podeUsarKitBlindagem}
          />

          <BlocoPermissoesEspeciais
            form={form}
            setForm={setForm}
            isAdmin={isAdmin}
            onSalvarHistorico={editando ? handleSalvarHistoricoPerms : null}
          />

          {isAdmin && (
            <BlocoHistoricoPermissoes
              historico={historicoPerms}
              onClear={async () => {
                if (!editando) return;
                if (!window.confirm('Apagar TODO o histórico desta licença?')) return;

                try {
                  await api.delete(`/licencas/${id}/permissoes-historico`);
                  setHistoricoPerms([]);
                } catch (e) {
                  console.error(e);
                  alert('Erro ao limpar histórico de permissões.');
                }
              }}
            />
          )}

          <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
            <Button variant="outlined" onClick={() => navigate("/licencas")}>
              Cancelar
            </Button>
            <Button variant="contained" color="primary" type="submit">
              {editando ? "Atualizar" : "Salvar"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
